import { Button, Modal, Progress } from '@affine/component';
import { workspaceApiFetch } from '@affine/core/modules/cloud';
import { useI18n } from '@affine/i18n';
import { ResetIcon } from '@blocksuite/icons/rc';
import { cssVarV2 } from '@streamlain/theme/v2';
import {
  type CSSProperties,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { embeddingProgress, embeddingProgressTitle } from './styles-css';

interface EmbeddingProgressProps {
  status: {
    embedded: number;
    total: number;
  } | null;
  workspaceId: string;
}

interface DocDetail {
  id: string;
  title: string;
  indexed: boolean;
  ignored: boolean;
}

// ─── Styles ─────────────────────────────────────────────────────────────────

const searchStyle: CSSProperties = {
  width: '100%',
  padding: '8px 12px',
  borderRadius: 6,
  border: '1px solid var(--streamlain-border-color)',
  backgroundColor: 'var(--streamlain-background-primary-color)',
  color: 'var(--streamlain-text-primary-color)',
  fontSize: 13,
  outline: 'none',
  marginBottom: 12,
};

const iconBtnStyle: CSSProperties = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: 4,
  borderRadius: 4,
  display: 'flex',
  alignItems: 'center',
  color: 'var(--streamlain-text-secondary-color)',
};

// ─── Data Fetching ──────────────────────────────────────────────────────────

async function gqlFetch(
  workspaceId: string,
  query: string,
  variables: Record<string, unknown>
) {
  const res = await workspaceApiFetch(
    '/graphql',
    {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query, variables }),
    },
    {
      workspaceId,
      category: 'metadata',
      key: `embedding:${query.slice(0, 48)}`,
      mode: 'background',
      priority: 'background',
    }
  );
  const json = await res.json();
  if (json.errors?.length) throw new Error(json.errors[0].message);
  return json.data;
}

async function fetchDocDetails(workspaceId: string): Promise<DocDetail[]> {
  const data = await gqlFetch(
    workspaceId,
    `query EmbeddingDocDetails($id: String!, $pagination: PaginationInput!) {
      workspace(id: $id) {
        indexedDocIds
        docs(pagination: $pagination) {
          edges { node { id title trashed } }
        }
        embedding { allIgnoredDocs { docId } }
      }
    }`,
    { id: workspaceId, pagination: { first: 500, offset: 0 } }
  );

  const workspace = data?.workspace;
  if (!workspace) return [];

  const indexedSet = new Set<string>(workspace.indexedDocIds ?? []);
  const ignoredSet = new Set<string>(
    (workspace.embedding?.allIgnoredDocs ?? []).map(
      (d: { docId: string }) => d.docId
    )
  );

  return (workspace.docs?.edges ?? [])
    .map(
      (e: { node: { id: string; title: string; trashed: boolean } }) => e.node
    )
    .filter(
      (d: { id: string; trashed: boolean }) =>
        d.id !== workspaceId && !d.trashed
    )
    .map((d: { id: string; title: string }) => ({
      id: d.id,
      title: d.title || 'Untitled',
      indexed: indexedSet.has(d.id),
      ignored: ignoredSet.has(d.id),
    }))
    .sort(
      (a: DocDetail, b: DocDetail) =>
        Number(a.ignored) - Number(b.ignored) ||
        Number(a.indexed) - Number(b.indexed) ||
        a.title.localeCompare(b.title)
    );
}

async function toggleDocIgnored(
  workspaceId: string,
  docId: string,
  exclude: boolean
): Promise<void> {
  await gqlFetch(
    workspaceId,
    `mutation ToggleDocIgnored($workspaceId: String!, $add: [String!], $remove: [String!]) {
      updateWorkspaceEmbeddingIgnoredDocs(workspaceId: $workspaceId, add: $add, remove: $remove)
    }`,
    {
      workspaceId,
      add: exclude ? [docId] : [],
      remove: exclude ? [] : [docId],
    }
  );
}

async function reindexDoc(workspaceId: string, docId: string): Promise<void> {
  await gqlFetch(
    workspaceId,
    `mutation ReindexDoc($workspaceId: String!, $docIds: [String!]) {
      reindexWorkspaceEmbeddings(workspaceId: $workspaceId, docIds: $docIds) {
        deletedChunks
        queuedDocs
      }
    }`,
    { workspaceId, docIds: [docId] }
  );
}

// ─── Doc Details Dialog ─────────────────────────────────────────────────────

function DocDetailsDialog({
  open,
  onClose,
  workspaceId,
}: {
  open: boolean;
  onClose: () => void;
  workspaceId: string;
}) {
  const [docs, setDocs] = useState<DocDetail[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [busyIds, setBusyIds] = useState<Set<string>>(new Set());

  const loadDocs = useCallback(() => {
    setLoading(true);
    fetchDocDetails(workspaceId)
      .then(setDocs)
      .catch(() => setDocs([]))
      .finally(() => setLoading(false));
  }, [workspaceId]);

  useEffect(() => {
    if (open) loadDocs();
  }, [open, loadDocs]);

  const filteredDocs = useMemo(() => {
    if (!search.trim()) return docs;
    const q = search.toLowerCase();
    return docs.filter(d => d.title.toLowerCase().includes(q));
  }, [docs, search]);

  const withBusy = useCallback(
    (docId: string, fn: () => Promise<void>) => {
      setBusyIds(prev => new Set(prev).add(docId));
      fn()
        .then(() => loadDocs())
        .catch(() => {})
        .finally(() => {
          setBusyIds(prev => {
            const next = new Set(prev);
            next.delete(docId);
            return next;
          });
        });
    },
    [loadDocs]
  );

  const indexedCount = docs.filter(d => d.indexed && !d.ignored).length;
  const activeCount = docs.filter(d => !d.ignored).length;
  const ignoredCount = docs.filter(d => d.ignored).length;

  return (
    <Modal
      open={open}
      onOpenChange={(v: boolean) => !v && onClose()}
      title="Dokumenten-Index"
      width={560}
    >
      <div style={{ padding: '0 0 16px 0' }}>
        <div
          style={{
            fontSize: 13,
            color: 'var(--streamlain-text-secondary-color)',
            marginBottom: 12,
          }}
        >
          {loading
            ? 'Lade...'
            : `${indexedCount}/${activeCount} indexiert${ignoredCount > 0 ? ` · ${ignoredCount} ausgeschlossen` : ''}`}
        </div>

        <input
          style={searchStyle}
          placeholder="Dokument suchen..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          autoFocus
        />

        <div style={{ maxHeight: 400, overflowY: 'auto' }}>
          {filteredDocs.length === 0 && !loading && (
            <div
              style={{
                textAlign: 'center',
                padding: 24,
                color: 'var(--streamlain-text-secondary-color)',
                fontSize: 13,
              }}
            >
              {search ? 'Keine Treffer' : 'Keine Dokumente'}
            </div>
          )}
          {filteredDocs.map(doc => (
            <div
              key={doc.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '8px 0',
                borderBottom: '1px solid var(--streamlain-border-color)',
                opacity: doc.ignored ? 0.5 : 1,
              }}
            >
              <div
                style={{
                  flex: 1,
                  minWidth: 0,
                  fontSize: 13,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
                title={doc.title}
              >
                {doc.title}
              </div>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 500,
                  padding: '2px 8px',
                  borderRadius: 4,
                  marginLeft: 8,
                  flexShrink: 0,
                  color: doc.ignored
                    ? 'var(--streamlain-text-disable-color)'
                    : doc.indexed
                      ? 'var(--streamlain-primary-color)'
                      : 'var(--streamlain-warning-color)',
                  backgroundColor: 'var(--streamlain-hover-color)',
                }}
              >
                {doc.ignored
                  ? 'Ausgeschlossen'
                  : doc.indexed
                    ? 'Indexiert'
                    : 'Ausstehend'}
              </div>
              {/* Re-index button (only for non-ignored docs) */}
              {!doc.ignored && (
                <button
                  type="button"
                  title="Neu indexieren"
                  disabled={busyIds.has(doc.id)}
                  onClick={() =>
                    withBusy(doc.id, () => reindexDoc(workspaceId, doc.id))
                  }
                  style={{
                    ...iconBtnStyle,
                    marginLeft: 4,
                    opacity: busyIds.has(doc.id) ? 0.3 : 1,
                  }}
                >
                  <ResetIcon width={14} height={14} />
                </button>
              )}
              <Button
                variant="plain"
                disabled={busyIds.has(doc.id)}
                onClick={() =>
                  withBusy(doc.id, () =>
                    toggleDocIgnored(workspaceId, doc.id, !doc.ignored)
                  )
                }
                style={{
                  fontSize: 11,
                  padding: '2px 8px',
                  marginLeft: 4,
                  flexShrink: 0,
                }}
              >
                {busyIds.has(doc.id)
                  ? '...'
                  : doc.ignored
                    ? 'Einschliessen'
                    : 'Ausschliessen'}
              </Button>
            </div>
          ))}
        </div>
      </div>
    </Modal>
  );
}

// ─── Main Component ─────────────────────────────────────────────────────────

const EmbeddingProgress: React.FC<EmbeddingProgressProps> = ({
  status,
  workspaceId,
}) => {
  const t = useI18n();
  const [dialogOpen, setDialogOpen] = useState(false);

  const loading = status === null;

  // Clamp embedded to total — stale embeddings from deleted docs can cause embedded > total
  const embedded = loading ? 0 : Math.min(status.embedded, status.total);
  const percent = loading
    ? 0
    : status.total === 0
      ? 1
      : embedded / status.total;
  const progress = Math.min(Math.round(percent * 100), 100);
  const synced = percent >= 1;

  return (
    <div className={embeddingProgress} data-testid="embedding-progress-wrapper">
      <div
        className={embeddingProgressTitle}
        data-testid="embedding-progress-title"
        data-progress={loading ? 'loading' : synced ? 'synced' : 'syncing'}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span>
            {loading
              ? t[
                  'com.streamlain.settings.workspace.indexer-embedding.embedding.progress.loading-sync-status'
                ]()
              : synced
                ? t[
                    'com.streamlain.settings.workspace.indexer-embedding.embedding.progress.synced'
                  ]()
                : t[
                    'com.streamlain.settings.workspace.indexer-embedding.embedding.progress.syncing'
                  ]()}
          </span>
          {!loading && status.total > 0 && (
            <button
              type="button"
              onClick={() => setDialogOpen(true)}
              data-testid="embedding-progress-details-toggle"
              style={{
                background: 'none',
                border: '1px solid var(--streamlain-border-color)',
                borderRadius: 4,
                padding: '1px 6px',
                fontSize: 11,
                cursor: 'pointer',
                color: 'var(--streamlain-text-secondary-color)',
              }}
            >
              Details
            </button>
          )}
        </div>
        {loading ? null : (
          <div
            data-testid="embedding-progress-count"
            style={{ display: 'flex', alignItems: 'center', gap: 8 }}
          >
            {`${embedded}/${status.total}`}
          </div>
        )}
      </div>
      <Progress
        testId="embedding-progress"
        value={progress}
        readonly
        style={{
          visibility: loading ? 'hidden' : 'visible',
          color: cssVarV2('text/primary'),
        }}
      />
      <DocDetailsDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        workspaceId={workspaceId}
      />
    </div>
  );
};

export default EmbeddingProgress;
